/**
 * Wire contract cirium-integration → live-activity worker: ONE flight voucher's
 * legs as they are right now, plus why this event was emitted.
 *
 * `legs` carries EVERY monitored leg (not only the one the alert is about): the
 * worker needs the onward leg to know a landing is a connection, and a leg with
 * no observation yet still needs its scheduled times to open its window.
 * `alert` is the notification policy's verdict (notified / would_notify): the
 * live card makes sound exactly when the traveler's message channel would.
 */
import { z } from 'zod';

import { EnvelopeOrgIdSchema } from '../conversation/org_id';
import { FlightLiveStatusSchema } from '../traveler/traveler_view';

const InstantSchema = z
  .string()
  .refine((value) => !Number.isNaN(Date.parse(value)), { message: 'Expected an ISO-8601 instant' });

export const LiveFlightChangeKindSchema = z.enum([
  'status',
  'departure_gate',
  'terminal',
  'baggage_claim',
  'departure_time',
  'arrival_time',
  'cancelled',
  'diverted',
  'connection_risk',
]);
export type LiveFlightChangeKind = z.infer<typeof LiveFlightChangeKindSchema>;

export const LiveFlightEventKindSchema = z.enum(['alert', 'window_open']);

export const LiveFlightLegSchema = z
  .object({
    /** Position in the voucher's monitored legs (cirium `importFlights`). */
    index: z.number().int().nonnegative(),
    carrier: z.string().min(1),
    flightNumber: z.string().min(1),
    departureAirport: z.string().min(3),
    arrivalAirport: z.string().min(3),
    /** UTC instants; the airline's rescheduled time when it moved the leg. */
    scheduledDeparture: InstantSchema,
    scheduledArrival: InstantSchema,
    live: FlightLiveStatusSchema.nullable(),
  })
  .strict();
export type LiveFlightLeg = z.infer<typeof LiveFlightLegSchema>;

export const LiveFlightEventSchema = z
  .object({
    version: z.literal(1),
    kind: LiveFlightEventKindSchema,
    eventKey: z.string().min(1).max(200),
    voucherId: z.string().min(1),
    voucherCode: z.string().min(1),
    /** Customer publicId (UUIDv5), never the legacy id. */
    customerId: z.string().min(1).max(200),
    orgId: EnvelopeOrgIdSchema,
    occurredAt: z.coerce.date(),
    /** The leg the alert is about; null when it names none. */
    legIndex: z.number().int().nonnegative().nullable(),
    alert: z.boolean(),
    changeKinds: z.array(LiveFlightChangeKindSchema),
    legs: z.array(LiveFlightLegSchema).min(1),
  })
  .strict();
export type LiveFlightEvent = z.infer<typeof LiveFlightEventSchema>;
export type LiveFlightEventInput = z.input<typeof LiveFlightEventSchema>;
